import { unavailableTmsOperation } from './tmsConnection';
import { buildCalComPublicBookingUrl, getCalComEmbedConfig, getBookingPaymentRuntimeConfig } from './calComGateway';
import { reportClientError } from './clientErrorReporter';
import {
  getPublicPaymentConfigRuntimeConfig,
  loadConsultationPaymentStatus,
  loadPublicPaymentConfig,
  resolvePublicCheckoutPath,
  sanitizePublicPaymentProvider,
} from './publicPaymentConfig';
import {
  attachPublicRequestMetadata,
  buildPublicRequestHeaders,
  resolvePublicRequestId,
} from './publicRequestReliability';
import { buildReachPublicApiUrl, getReachPublicApiBaseUrl } from './reachPublicApi';

const API_BASE_URL = getReachPublicApiBaseUrl();
const DEFAULT_OWNER_UID = String(
  process.env.REACT_APP_CARRIER_FLOW_OWNER_UID
  || process.env.REACT_APP_PUBLIC_INTAKE_OWNER_UID
  || ''
).trim();
const DEFAULT_WORKSPACE_OWNER_UID = String(
  process.env.REACT_APP_CARRIER_FLOW_WORKSPACE_OWNER_UID
  || process.env.REACT_APP_PUBLIC_INTAKE_WORKSPACE_OWNER_UID
  || ''
).trim();

const PAYPAL_CREATE_ORDER_PATH = String(
  process.env.REACT_APP_CARRIER_FLOW_PAYPAL_BOOKING_CREATE_ORDER_PATH
  || '/api/public/consultations/paypal/create-order'
).trim();
const PAYPAL_CAPTURE_ORDER_PATH = String(
  process.env.REACT_APP_CARRIER_FLOW_PAYPAL_BOOKING_CAPTURE_ORDER_PATH
  || '/api/public/consultations/paypal/capture-order'
).trim();
const STRIPE_CREATE_CHECKOUT_SESSION_PATH = String(
  process.env.REACT_APP_CARRIER_FLOW_STRIPE_BOOKING_CREATE_CHECKOUT_SESSION_PATH
  || '/api/public/consultations/stripe/create-checkout-session'
).trim();
const SWIFT_START_PATH = String(
  process.env.REACT_APP_CARRIER_FLOW_SWIFT_BOOKING_START_PATH
  || '/api/public/consultations/swift/start'
).trim();
const CALENDAR_AVAILABILITY_PATH = String(
  process.env.REACT_APP_CARRIER_FLOW_CALENDAR_AVAILABILITY_PATH
  || '/api/public/consultations/availability'
).trim();
const CALENDAR_BOOKING_PATH = String(
  process.env.REACT_APP_CARRIER_FLOW_CALENDAR_BOOKING_PATH
  || '/api/public/consultations/book'
).trim();
const CALENDAR_PROVIDER = String(
  process.env.REACT_APP_BOOKING_PROVIDER
  || 'calcom'
).trim().toLowerCase();
const CALENDAR_EVENT_TYPE = String(
  process.env.REACT_APP_BOOKING_EVENT_TYPE
  || 'book-a-meeting'
).trim();
const CALENDAR_PUBLIC_LINK = String(
  process.env.REACT_APP_BOOKING_PUBLIC_LINK
  || process.env.REACT_APP_CALCOM_PUBLIC_EVENT_URL
  || ''
).trim();

const BOOKING_PAYMENT_MODE = String(
  process.env.REACT_APP_BOOKING_PAYMENT_MODE
  || 'carrierflow_paypal'
).trim().toLowerCase();

const BOOKING_MEETING_FEE_AMOUNT = Number(process.env.REACT_APP_BOOKING_MEETING_FEE_AMOUNT || 0);
const BOOKING_MEETING_FEE_CURRENCY = String(process.env.REACT_APP_BOOKING_MEETING_FEE_CURRENCY || 'USD').trim() || 'USD';
const BOOKING_MEETING_FEE_LABEL = String(process.env.REACT_APP_BOOKING_MEETING_FEE_LABEL || 'Consultation Review').trim() || 'Consultation Review';
const BOOKING_PAYMENT_REQUIRED = String(process.env.REACT_APP_BOOKING_PAYMENT_REQUIRED || '').trim().toLowerCase() === 'true';
const API_REQUEST_TIMEOUT_MS = Number(
  process.env.REACT_APP_CARRIER_FLOW_API_TIMEOUT_MS || process.env.REACT_APP_REQUEST_TIMEOUT_MS || 12000
) || 12000;

const OWNER_ROUTING_KEYS = new Set(['ownerUid', 'workspaceOwnerUid']);
const PRIVILEGED_PUBLIC_LEAD_KEYS = new Set([
  'ownerUid',
  'workspaceOwnerUid',
  'status',
  'leadId',
  'convertedLeadId',
  'conversionDecision',
  'reviewDecision',
  'consultationPaymentStatus',
  'consultationPaymentAmount',
  'consultationPaymentCurrency',
  'consultationPaymentProvider',
  'consultationPaymentOrderId',
  'consultationPaymentCaptureId',
  'consultationBookingId',
  'bookingId',
  'paymentEventId',
  'paidAt',
  'scheduleStatus',
  'requestedMeetingStart',
  'requestedMeetingEnd',
  'calComBookingUrl',
  'googleCalendarEventId',
  'calendarProvider',
  'calendarLinkId',
  'consultationConfirmedAt',
]);

function sanitizeText(value) {
  return String(value || '').trim();
}

function stripPayloadKeys(payload = {}, blockedKeys = OWNER_ROUTING_KEYS) {
  return Object.entries(payload || {}).reduce((result, [key, value]) => {
    if (!blockedKeys.has(key)) {
      result[key] = value;
    }
    return result;
  }, {});
}

function requireApiBaseUrl() {
  if (!API_BASE_URL) {
    throw new Error(
      'Website service configuration is missing. Please contact support.'
    );
  }
  return API_BASE_URL;
}

function buildApiUrl(path) {
  requireApiBaseUrl();
  return buildReachPublicApiUrl(path);
}

function getOwnerPayload() {
  const ownerUid = DEFAULT_WORKSPACE_OWNER_UID || DEFAULT_OWNER_UID;
  if (!ownerUid) {
    return {};
  }

  return DEFAULT_WORKSPACE_OWNER_UID
    ? { workspaceOwnerUid: DEFAULT_WORKSPACE_OWNER_UID }
    : { ownerUid: DEFAULT_OWNER_UID };
}

async function parseJsonResponse(response) {
  const text = await response.text();
  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    console.error('Website service response was not valid JSON:', error);
    throw new Error('The website service returned an invalid response.');
  }
}

async function requestJson() {
  return unavailableTmsOperation();
}

function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch (error) {
    return undefined;
  }
}

function normalizeMoney(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) {
    return 0;
  }
  return Number(numeric.toFixed(2));
}

function getConsultationPaymentProviderFromMode(mode = BOOKING_PAYMENT_MODE) {
  const normalizedMode = sanitizeText(mode).toLowerCase();
  if (normalizedMode.includes('stripe')) return 'stripe';
  if (normalizedMode.includes('swift')) return 'swift';
  if (normalizedMode.includes('paypal')) return 'paypal';
  return 'manual';
}

function stripClientOnlyPaymentConfig(payload = {}) {
  const {
    paymentConfig,
    publicPaymentConfig,
    checkoutConfig,
    ...safePayload
  } = payload || {};
  return safePayload;
}

export function getConsultationOfferConfig() {
  const amount = normalizeMoney(BOOKING_MEETING_FEE_AMOUNT);
  return {
    amount,
    currency: BOOKING_MEETING_FEE_CURRENCY,
    label: BOOKING_MEETING_FEE_LABEL,
    paymentRequired: BOOKING_PAYMENT_REQUIRED || amount > 0,
    paymentProvider: getConsultationPaymentProviderFromMode(),
    usesCarrierFlowPayPal: BOOKING_PAYMENT_MODE === 'carrierflow_paypal',
    usesCalComStripe: BOOKING_PAYMENT_MODE === 'calcom_stripe',
  };
}

export function formatConsultationMoney(amount = 0, currency = 'USD') {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (error) {
    return `${currency} ${amount}`;
  }
}

export function buildConsultationCalendarUrl(context = {}) {
  return buildCalComPublicBookingUrl({
    ...context,
    paymentMode: context.bookingPaymentMode || BOOKING_PAYMENT_MODE,
  });
}

export function buildBookMeetingLeadPayload(form = {}, options = {}) {
  const companyName = sanitizeText(form.companyName || form.company || options.companyName);
  const contactName = sanitizeText(form.contactName || form.name || options.contactName);
  const email = sanitizeText(form.email || options.email);
  const phone = sanitizeText(form.phone || options.phone);
  const timezone = sanitizeText(form.timezone || options.timezone) || getBrowserTimezone();
  const requestedMeetingStart = sanitizeText(form.requestedMeetingStart || options.requestedMeetingStart);
  const requestedMeetingEnd = sanitizeText(form.requestedMeetingEnd || options.requestedMeetingEnd);
  const slotLabel = sanitizeText(form.slotLabel || options.slotLabel);
  const slotSource = sanitizeText(form.slotSource || options.slotSource) || CALENDAR_PROVIDER;
  const consultationPaymentAmount = normalizeMoney(
    form.consultationPaymentAmount || options.consultationPaymentAmount || 0
  );
  const consultationPaymentCurrency = sanitizeText(
    form.consultationPaymentCurrency || options.consultationPaymentCurrency || 'USD'
  ) || 'USD';
  const notes = [
    'Source page: Book a meeting',
    slotLabel ? `Selected slot: ${slotLabel}` : '',
    requestedMeetingStart ? `Requested meeting start: ${requestedMeetingStart}` : '',
    requestedMeetingEnd ? `Requested meeting end: ${requestedMeetingEnd}` : '',
    sanitizeText(form.notes || options.notes) ? `Notes: ${sanitizeText(form.notes || options.notes)}` : '',
  ].filter(Boolean).join('\n\n');

  return {
    companyName: companyName || contactName || 'Book a meeting inquiry',
    contactName,
    email,
    phone,
    interests: ['other'],
    otherInterest: 'book_a_meeting',
    notes,
    consultationPaymentStatus: sanitizeText(form.consultationPaymentStatus || options.consultationPaymentStatus) || 'not_started',
    consultationPaymentAmount,
    consultationPaymentCurrency,
    consultationPaymentProvider: sanitizePublicPaymentProvider(
      form.consultationPaymentProvider || options.consultationPaymentProvider,
      getConsultationPaymentProviderFromMode()
    ),
    consultationPaymentOrderId: sanitizeText(form.consultationPaymentOrderId || options.consultationPaymentOrderId),
    consultationPaymentCaptureId: sanitizeText(form.consultationPaymentCaptureId || options.consultationPaymentCaptureId),
    scheduleStatus: sanitizeText(form.scheduleStatus || options.scheduleStatus) || (requestedMeetingStart ? 'selected' : 'not_selected'),
    requestedMeetingStart,
    requestedMeetingEnd,
    timezone,
    bookingProvider: slotSource,
    bookingEventType: sanitizeText(form.bookingEventType || options.bookingEventType) || CALENDAR_EVENT_TYPE,
    bookingPaymentMode: sanitizeText(form.bookingPaymentMode || options.bookingPaymentMode) || BOOKING_PAYMENT_MODE,
  };
}

export async function createBookMeetingLead(form = {}, options = {}) {
  const payload = buildBookMeetingLeadPayload(form, options);
  return requestJson('/api/public/leads', {
    method: 'POST',
    payload: stripPayloadKeys(payload, PRIVILEGED_PUBLIC_LEAD_KEYS),
  });
}

export async function loadConsultationPaymentConfig(options = {}) {
  return loadPublicPaymentConfig({
    ...options,
    flow: 'consultation',
  });
}

export async function confirmConsultationPaymentStatus(options = {}) {
  return loadConsultationPaymentStatus(options);
}

export async function createConsultationPayPalOrder(payload = {}, options = {}) {
  const paymentConfig = options.paymentConfig || payload.paymentConfig || payload.publicPaymentConfig;
  const checkoutPath = resolvePublicCheckoutPath(
    paymentConfig,
    'paypal',
    'createOrderPath',
    PAYPAL_CREATE_ORDER_PATH
  );
  return requestJson(checkoutPath, {
    method: 'POST',
    payload: {
      eventType: CALENDAR_EVENT_TYPE,
      bookingProvider: CALENDAR_PROVIDER,
      timezone: getBrowserTimezone(),
      ...stripClientOnlyPaymentConfig(payload),
    },
  });
}

export async function captureConsultationPayPalOrder(payload = {}, options = {}) {
  const paymentConfig = options.paymentConfig || payload.paymentConfig || payload.publicPaymentConfig;
  const checkoutPath = resolvePublicCheckoutPath(
    paymentConfig,
    'paypal',
    'captureOrderPath',
    PAYPAL_CAPTURE_ORDER_PATH
  );
  return requestJson(checkoutPath, {
    method: 'POST',
    payload: {
      eventType: CALENDAR_EVENT_TYPE,
      bookingProvider: CALENDAR_PROVIDER,
      timezone: getBrowserTimezone(),
      ...stripClientOnlyPaymentConfig(payload),
    },
  });
}

export async function createConsultationStripeCheckoutSession(payload = {}, options = {}) {
  const paymentConfig = options.paymentConfig || payload.paymentConfig || payload.publicPaymentConfig;
  const checkoutPath = resolvePublicCheckoutPath(
    paymentConfig,
    'stripe',
    'createCheckoutSessionPath',
    STRIPE_CREATE_CHECKOUT_SESSION_PATH
  );
  return requestJson(checkoutPath, {
    method: 'POST',
    payload: {
      eventType: CALENDAR_EVENT_TYPE,
      bookingProvider: CALENDAR_PROVIDER,
      timezone: getBrowserTimezone(),
      ...stripClientOnlyPaymentConfig(payload),
    },
  });
}

export async function startConsultationSwiftPayment(payload = {}, options = {}) {
  const paymentConfig = options.paymentConfig || payload.paymentConfig || payload.publicPaymentConfig;
  const checkoutPath = resolvePublicCheckoutPath(
    paymentConfig,
    'swift',
    'startPath',
    SWIFT_START_PATH
  );
  return requestJson(checkoutPath, {
    method: 'POST',
    payload: {
      eventType: CALENDAR_EVENT_TYPE,
      bookingProvider: CALENDAR_PROVIDER,
      timezone: getBrowserTimezone(),
      ...stripClientOnlyPaymentConfig(payload),
    },
  });
}

export async function getConsultationAvailability(params = {}) {
  const searchParams = new URLSearchParams();
  const eventType = sanitizeText(params.eventType || CALENDAR_EVENT_TYPE);
  const timezone = sanitizeText(params.timezone || getBrowserTimezone());
  const startDate = sanitizeText(params.startDate);
  const endDate = sanitizeText(params.endDate);

  if (eventType) searchParams.set('eventType', eventType);
  if (timezone) searchParams.set('timezone', timezone);
  if (startDate) searchParams.set('startDate', startDate);
  if (endDate) searchParams.set('endDate', endDate);

  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
  return requestJson(`${CALENDAR_AVAILABILITY_PATH}${suffix}`, { method: 'GET' });
}

export async function confirmConsultationBooking(payload = {}) {
  return requestJson(CALENDAR_BOOKING_PATH, {
    method: 'POST',
    payload: {
      eventType: CALENDAR_EVENT_TYPE,
      bookingProvider: CALENDAR_PROVIDER,
      timezone: getBrowserTimezone(),
      ...payload,
    },
  });
}

export function getConsultationRuntimeConfig() {
  return {
    apiBaseUrl: API_BASE_URL,
    ownerUid: DEFAULT_OWNER_UID,
    workspaceOwnerUid: DEFAULT_WORKSPACE_OWNER_UID,
    bookingProvider: CALENDAR_PROVIDER,
    bookingEventType: CALENDAR_EVENT_TYPE,
    bookingPublicLink: CALENDAR_PUBLIC_LINK,
    paypalCreateOrderPath: PAYPAL_CREATE_ORDER_PATH,
    paypalCaptureOrderPath: PAYPAL_CAPTURE_ORDER_PATH,
    stripeCreateCheckoutSessionPath: STRIPE_CREATE_CHECKOUT_SESSION_PATH,
    swiftStartPath: SWIFT_START_PATH,
    calendarAvailabilityPath: CALENDAR_AVAILABILITY_PATH,
    calendarBookingPath: CALENDAR_BOOKING_PATH,
    bookingPaymentMode: BOOKING_PAYMENT_MODE,
    offer: getConsultationOfferConfig(),
    calCom: getCalComEmbedConfig(),
    payment: getBookingPaymentRuntimeConfig(),
    publicPayment: getPublicPaymentConfigRuntimeConfig(),
  };
}
